import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useProjectsStore } from '@/store/projects';
import { useToast } from '@/hooks/use-toast';
import BootScreen from '@/components/BootScreen';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';

export default function NewPlotRedirect() {
  const { user, initializing, defaultProjectId } = useAuth();
  const { getOrCreateDefaultProject } = useProjectsStore();
  const { go } = useNav();
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const did = useRef(false);

  useEffect(() => {
    if (did.current) return;
    if (initializing) return;
    if (!user) return; // RequireAuth guards this, but keep passive

    did.current = true;
    (async () => {
      try {
        setError(null);
        const unbound = (() => {
          try {
            const v = (import.meta as any).env?.VITE_EDITOR_UNBOUND_ENTRY || (process as any)?.env?.EDITOR_UNBOUND_ENTRY || 'false';
            return String(v).toLowerCase() === 'true';
          } catch { return false; }
        })();
        const url = new URL(window.location.href);
        const search = url.search || '';
        const qs = search && search.startsWith('?') ? search : (search ? `?${search}` : '');
        if (unbound) {
          const pid = defaultProjectId;
          if (pid) {
            go(routes.plotEditor.open(pid) + qs);
          } else {
            go(routes.dashboard());
          }
          return;
        }
        const project = await getOrCreateDefaultProject();
        if (!project?.id) throw new Error('No default project');
        go(routes.plotEditor.open(project.id) + qs);
      } catch (e: any) {
        const msg = String(e?.message || '');
        if (msg === 'no_default_project') {
          try { toast({ title: 'No project yet', description: 'Create your first project from the dashboard.' }); } catch {}
          go(routes.dashboard());
          return;
        }
        setError(msg || 'Failed to open editor');
      }
    })();
  }, [initializing, user, defaultProjectId, getOrCreateDefaultProject, go]);

  if (error) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-4">
          <h2 className="text-2xl font-semibold">Couldn’t open the editor</h2>
          <p className="text-muted-foreground">{error}</p>
          <div className="flex gap-3 justify-center">
            <button className="bg-primary text-primary-foreground px-4 py-2 rounded" onClick={() => window.location.reload()}>Try again</button>
            <button className="px-4 py-2 rounded border" onClick={() => go(routes.home())}>Go to Home</button>
          </div>
        </div>
      </div>
    );
  }

  return <BootScreen />;
}


